import type { ReactElement } from 'react';
import React from 'react';
import { Form, Formik } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import IconButton from 'atom-components/buttons/icon-button';
import filterTopbarSchema from 'pages/clusters/subpages/aggregated-reports-tab/filter-topbar/filter-topbar.schema';
import type { FormValues } from 'pages/clusters/types';
import { useIsSchool } from 'services/providers/auth.hooks';
import AreaField, { initialArea } from './area-field';
import ClusterField, { initialCluster } from './cluster-field';
import DateRangeField, { initialDateRange } from './date-range-field';
import TestField, { initialTest } from './test-field';

const INITIAL_VALUES = {
  ...initialCluster,
  ...initialDateRange,
  ...initialArea,
  ...initialTest
};

type Props = { initialValues: FormValues; onSubmit(args: FormValues): void; isLoading: boolean };

/**
 * Entry filters for cluster reporting.
 *
 * Requires selection of:
 *  - Cluster
 *  - Data range - year/period of start/end
 *  - Area
 *  - Tests - one or more
 *
 * The form values are synced with the url on submission
 */
export default function FilterTopbar(props: Props): ReactElement {
  const { initialValues, isLoading, onSubmit } = props;
  const isSchool = useIsSchool();

  return (
    <Formik<FormValues>
      initialValues={initialValues || INITIAL_VALUES}
      validationSchema={toFormikValidationSchema(filterTopbarSchema(isSchool))}
      onSubmit={(values, { setSubmitting }): void => {
        const valuesWithTest = { ...values, test: values.test.filter((v) => !!+v) };
        onSubmit(valuesWithTest);
        setSubmitting(false);
      }}
      validateOnBlur
    >
      {({ isSubmitting, submitCount }): ReactElement => (
        <Form>
          <div className="card-body d-flex">
            <div className="w-100 mx-2">
              {/* Cluster selection */}
              <ClusterField />
              {/* Period select or date range select */}
              <DateRangeField isSchool={isSchool} />
            </div>
            <div className="w-100 mx-2">
              {/* Area & test selection */}
              <AreaField />
              <TestField />
            </div>
          </div>
          <div className="card-footer text-end d-print-none">
            {/**
             * Submission button changes depending on state
             * - disabled with spinner when: loading or submitting
             * - text depends on state as well
             */}
            <IconButton
              type="submit"
              color="info"
              icon={isSubmitting || isLoading ? 'spinner' : 'play'}
              spin={isSubmitting || isLoading}
              disabled={isSubmitting || isLoading}
            >
              {isLoading && 'Fetching data'}
              {!isLoading && !submitCount && 'Generate Reports'}
              {!isLoading && !!submitCount && 'Update Reports'}
            </IconButton>
          </div>
        </Form>
      )}
    </Formik>
  );
}
